import { useState, useEffect, useMemo, useContext, useCallback, useRef } from 'react';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Skeleton } from 'primereact/skeleton';
import { BiSearch } from 'react-icons/bi';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { Dialog } from 'primereact/dialog';
import { InputSwitch } from 'primereact/inputswitch';
import AlertService from '../../../services/AlertService';
import AuthContext from '../../../auth/AuthContext';
import DocumentTypeService from '../../../services/DocumentTypeService';
import CourseService from '../../../services/CourseService';
import DocumentService from '../../../services/DocumentService';

const CourseDocuments = () => {
  const authContext = useContext(AuthContext);
  const { filters } = useOutletContext();
  const navigate = useNavigate();
  const params = useParams();
  const { state } = useLocation();
  const submitControllerRef = useRef(null);

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const courseId = params.id || state?.id;

  const [leftItems, setLeftItems] = useState([]);
  const [rightItems, setRightItems] = useState([]);
  const [initialRightItems, setInitialRightItems] = useState([]);
  const [searchLeft, setSearchLeft] = useState('');
  const [searchRight, setSearchRight] = useState('');
  const [selectAllLeft, setSelectAllLeft] = useState(false);
  const [selectAllRight, setSelectAllRight] = useState(false);
  const [showChangesModal, setShowChangesModal] = useState(false);
  const [assigned, setAssigned] = useState([]);
  const [removed, setRemoved] = useState([]);
  const [statusChanged, setStatusChanged] = useState([]);

const fetchDocumentTags = useCallback(async (signal) => { 
    try {
      const res = await DocumentTypeService.GetDocumentTags(
        authContext,
        signal 
      );

      if (signal?.aborted) return; 

      if (res?.isSuccess && Array.isArray(res.data)) {
        setCategories(['All', ...res.data.map(m => m.label)]);
      } else {
        setCategories(['All']);
      }
    } catch (error) {
      if (error.name === "AbortError") return; 
      setCategories(['All']);
      AlertService.error('Failed to fetch document tags');
    }
  }, [authContext]);

const loadCourseDocuments = useCallback(async (signal) => { 
    try {
      setLoading(true);

      const docData = await DocumentService.GetAll('', authContext, signal); 

      if (signal?.aborted) return; 

      if (!docData.isSuccess) {
        AlertService.error(docData.errorMessage);
        return;
      }

      const data = await CourseService.GetAttachments(
        courseId,
        '',
        authContext,
        signal 
      );

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        const attachedDocIds = data.data.map(m => m.documentId);
        const newLeftItems = docData.data
          .filter(f => !attachedDocIds.includes(f.id))
          .map(m => ({
            id: m.id,
            label: m.documentName,
            checked: false,
            category: m.taggedUnderDesc || 'Uncategorized',
          }));

        const newRightItems = data.data.map(m => ({
          id: m.documentId,
          label: m.documentName,
          checked: false,
          category: m.documentTag || 'Uncategorized',
          status: m.status,
        }));

        setLeftItems(newLeftItems);
        setRightItems(newRightItems);
        setInitialRightItems(newRightItems.map(i => ({ ...i })));
      } else {
        setLeftItems(docData.data.map(m => ({
          id: m.id,
          label: m.documentName,
          checked: false,
          category: m.taggedUnderDesc || 'Uncategorized',
        })));
        setRightItems([]);
        setInitialRightItems([]);
        AlertService.error(data.errorMessage);
      }
    } catch (error) {
      if (error.name === "AbortError") return; 
      AlertService.error('Failed to load documents');
    } finally {
      if (!signal?.aborted) setLoading(false); 
    }
  }, [courseId, authContext]);

useEffect(() => {
    const controller = new AbortController(); 

    const loadData = async () => {
      await Promise.all([
        fetchDocumentTags(controller.signal), 
        loadCourseDocuments(controller.signal) 
      ]);
    };

    loadData();

    return () => controller.abort(); 
  }, [fetchDocumentTags, loadCourseDocuments]);



  useEffect(() => {
    const currentIds = new Set(rightItems.map(i => i.id));

    const newlyAssigned = rightItems.filter(item =>
      !initialRightItems.some(init => init.id === item.id)
    );

    const removedItems = initialRightItems.filter(init =>
      !currentIds.has(init.id)
    );

    const changedStatus = rightItems
      .filter(item => {
        const init = initialRightItems.find(i => i.id === item.id);
        return init && init.status !== item.status;
      })
      .map(item => ({ ...item }));

    setAssigned(newlyAssigned);
    setRemoved(removedItems);
    setStatusChanged(changedStatus);
  }, [rightItems, initialRightItems]);

  const hasChanges = useMemo(
    () => assigned.length > 0 || removed.length > 0 || statusChanged.length > 0,
    [assigned, removed, statusChanged]
  );

  // Filters
  const activeFilters = Object.keys(filters).filter(cat => filters[cat]);

  const filteredLeftItems = useMemo(() => {
    if (activeFilters.length === 0 || activeFilters.includes('All')) return leftItems;
    return leftItems.filter(item => activeFilters.includes(item.category));
  }, [leftItems, activeFilters]);

  const filteredRightItems = useMemo(() => {
    if (activeFilters.length === 0 || activeFilters.includes('All')) return rightItems;
    return rightItems.filter(item => activeFilters.includes(item.category));
  }, [rightItems, activeFilters]);

  const groupedLeftItems = useMemo(() => {
    const groups = {};
    categories.filter(cat => cat !== 'All').forEach(category => {
      groups[category] = filteredLeftItems
        .filter(item => item.category === category)
        .filter(item => item.label.toLowerCase().includes(searchLeft.toLowerCase()));
    });
    return groups;
  }, [filteredLeftItems, categories, searchLeft]);

  const groupedRightItems = useMemo(() => {
    const groups = {};
    categories.filter(cat => cat !== 'All').forEach(category => {
      groups[category] = filteredRightItems
        .filter(item => item.category === category)
        .filter(item => item.label.toLowerCase().includes(searchRight.toLowerCase()));
    });
    return groups;
  }, [filteredRightItems, categories, searchRight]);

  const handleCategoryCheckLeft = (category, checked) => {
    setLeftItems(current =>
      current.map(item =>
        category === 'All' || item.category === category
          ? { ...item, checked }
          : item
      )
    );
  };

  const handleCategoryCheckRight = (category, checked) => {
    setRightItems(current =>
      current.map(item =>
        item.category === category ? { ...item, checked } : item
      )
    );
  };

  const handleMove = direction => {
    if (direction === 'right') {
      const selected = leftItems.filter(i => i.checked);
      const moved = selected.map(i => ({ ...i, checked: false, status: true }));
      setRightItems(prev => [...prev, ...moved]);
      setLeftItems(prev => prev.filter(i => !i.checked));
    } else {
      const selected = rightItems.filter(i => i.checked);
      const moved = selected.map(i => ({
        id: i.id,
        label: i.label,
        checked: false,
        category: i.category,
      }));
      setLeftItems(prev => [...prev, ...moved]);
      setRightItems(prev => prev.filter(i => !i.checked));
    }
  };

  const toggleSelectAll = panel => {
    if (panel === 'left') {
      const newVal = !selectAllLeft;
      setSelectAllLeft(newVal);
      setLeftItems(prev => prev.map(i => ({ ...i, checked: newVal })));
    } else {
      const newVal = !selectAllRight;
      setSelectAllRight(newVal);
      setRightItems(prev => prev.map(i => ({ ...i, checked: newVal })));
    }
  };


  const handleToggleStatus = useCallback(id => {
    setRightItems(prev =>
      prev.map(item =>
        item.id === id
          ? { ...item, status: !item.status }
          : item
      )
    );
  }, []);

  // Sync header checkboxes
  useEffect(() => {
    setSelectAllLeft(leftItems.length > 0 && leftItems.every(i => i.checked));
  }, [leftItems]);

  useEffect(() => {
    setSelectAllRight(rightItems.length > 0 && rightItems.every(i => i.checked));
  }, [rightItems]);

  const handleCancel = () => navigate(-1);

  const handlePreviewClick = e => {
    e.preventDefault();
    if (!hasChanges) {
      AlertService.warning('No changes to preview');
      return;
    }
    setShowChangesModal(true);
  };


// Component unmount-ல் cancel
useEffect(() => {
    return () => {
      submitControllerRef.current?.abort(); 
    };
  }, []);

const confirmSubmit = async () => {
    if (submitControllerRef.current) {
      submitControllerRef.current.abort(); 
    }
    const controller = new AbortController();
    submitControllerRef.current = controller;

    setShowChangesModal(false);

    const payload = rightItems.map(item => ({
      courseId: parseInt(courseId),
      documentId: parseInt(item.id),
      status: item.status
    }));

    try {
      const result = await CourseService.SaveAttachment(
        payload,
        authContext,
        controller.signal 
      );

      if (controller.signal.aborted) return; 

      if (result.isSuccess) {
        AlertService.success('Document assignments updated successfully');
        await loadCourseDocuments();
      } else {
        AlertService.error(result.errorMessage || 'Failed to save');
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
      AlertService.error('Save failed');
    }
  };

  const modalFooter = (
    <div className='pt-2'>
      <button className="btn btn-outline-secondary px-5 py-2 me-4" onClick={() => setShowChangesModal(false)}>
        Back to Edit
      </button>
      <button className="btn btn-primary px-5 py-2" onClick={confirmSubmit}>
        Submit
      </button>
    </div>
  );

  const renderSkeletonCategory = (isRightPanel = false) => (
    <div className="mb-2">
      <div className="d-flex align-items-center pb-2 pt-2 ps-3 border-bottom">
        <Skeleton shape="circle" size="1.5rem" className="me-2" />
        <Skeleton width="10rem" height="1.2rem" />
      </div>
      {[...Array(7)].map((_, i) => (
        <div
          key={i}
          className="d-flex align-items-center pb-2 pt-2 ps-3 border-bottom"
          style={{ justifyContent: isRightPanel ? 'space-between' : 'flex-start' }}
        >
          <div className="d-flex align-items-center">
            <Skeleton shape="circle" size="1.5rem" className="ms-4" />
            <Skeleton width="40rem" height="1.2rem" className="ms-2" />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="container-fluid custom-container mt-4 mb-3">
      <div className="row g-1">
        <div className="col-12 col-md border rounded card p-0" style={{ minWidth: '300px' }}>
          <div className="d-flex justify-content-between align-items-center p-2 ps-3 border-bottom bg-section rounded-3">
            <div className="d-flex align-items-center">
              {loading ? (
                <Skeleton shape="circle" size="1.5rem" className="me-2" />
              ) : (
                <Checkbox checked={selectAllLeft} onChange={() => toggleSelectAll('left')} className="me-2" />
              )}
              <span className="fw-bold">Master's Repository</span>
            </div>
            <div className="position-relative">
              {loading ? (
                <Skeleton width="10rem" height="2rem" />
              ) : (
                <>
                  <input
                    type="text"
                    className="form-control custom-input-color"
                    placeholder="Search..."
                    value={searchLeft}
                    onChange={e => setSearchLeft(e.target.value)}
                  />
                  <BiSearch className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted" />
                </>
              )}
            </div>
          </div>
          <div className="pt-1 scroll-no-buttons" style={{ height: '660px', overflowY: 'auto' }}>
            {loading ? (
              <>
                {renderSkeletonCategory()}
                {renderSkeletonCategory()}
              </>
            ) : (
              categories.filter(cat => cat !== 'All').map(category => (
                groupedLeftItems[category]?.length > 0 && (
                  <div key={`left-${category}`} className="mb-0">
                    <div className="d-flex align-items-center pb-3 pt-3 ps-3 border-bottom" style={{ cursor: 'pointer' }}>
                      <Checkbox
                        checked={groupedLeftItems[category].every(i => i.checked)}
                        onChange={e => handleCategoryCheckLeft(category, e.checked)}
                        onClick={e => e.stopPropagation()}
                      />
                      <span className="ms-2 fw-bold">{category}</span>
                    </div>
                    {groupedLeftItems[category].map(item => (
  <div
    key={item.id}
    className="d-flex align-items-center pb-2 pt-2 px-3 border-bottom position-relative"
    style={{ minHeight: '56px' }}
  >
    <Checkbox
      className="ms-4 flex-shrink-0"
      checked={item.checked}
      onChange={e =>
        setLeftItems(current =>
          current.map(i => (i.id === item.id ? { ...i, checked: e.checked } : i))
        )
      }
    />
    <span 
      className="ms-3 text-break hyphens-auto flex-grow-1"
      style={{ 
        wordBreak: 'break-word', 
        lineHeight: '1.4',
        overflowWrap: 'break-word'
      }}
    >
      {item.label}
    </span>
  </div>
))}
                  </div>
                )
              ))
            )}
          </div>
        </div>

        {/* ARROWS */}
        <div className="col-12 col-md-2 col-lg-1 d-flex flex-md-column justify-content-center align-items-center gap-3">
          {loading ? (
            <>
              <Skeleton width="60px" height="2.5rem" className="mb-3" />
              <Skeleton width="60px" height="2.5rem" />
            </>
          ) : (
            <>
              <Button
                icon="pi pi-arrow-right"
                className="btn border-0 fw-bold"
                style={{ backgroundColor: '#9CF', minWidth: '60px' }}
                onClick={() => handleMove('right')}
                disabled={!leftItems.some(i => i.checked)}
              />
              <Button
                icon="pi pi-arrow-left"
                className="btn border-0 fw-bold"
                style={{ backgroundColor: '#9CF', minWidth: '60px' }}
                onClick={() => handleMove('left')}
                disabled={!rightItems.some(i => i.checked)}
              />
            </>
          )}
        </div>
        <div className="col-12 col-md border rounded card p-0" style={{ minWidth: '300px' }}>
          <div className="d-flex justify-content-between align-items-center p-2 ps-3 border-bottom bg-section rounded-3">
            <div className="d-flex align-items-center">
              {loading ? (
                <Skeleton shape="circle" size="1.5rem" className="me-2" />
              ) : (
                <Checkbox checked={selectAllRight} onChange={() => toggleSelectAll('right')} className="me-2" />
              )}
              <span className="fw-bold">Selected Repository</span>
            </div>
            <div className="position-relative">
              {loading ? (
                <Skeleton width="10rem" height="2rem" />
              ) : (
                <>
                  <input
                    type="text"
                    className="form-control custom-input-color"
                    placeholder="Search..."
                    value={searchRight}
                    onChange={e => setSearchRight(e.target.value)}
                  />
                  <BiSearch className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted" />
                </>
              )}
            </div>
          </div>
          <div className="pt-1 scroll-no-buttons" style={{ height: '660px', overflowY: 'auto' }}>
            {loading ? (
              <>
                {renderSkeletonCategory(true)}
                {renderSkeletonCategory(true)}
              </>
            ) : (
              categories
                .filter(cat => cat !== 'All')
                .map(category => (
                  groupedRightItems[category]?.length > 0 && (
                    <div key={`right-${category}`} className="mb-0">
                      <div className="d-flex align-items-center pb-3 pt-3 ps-3 border-bottom" style={{ cursor: 'pointer' }}>
                        <Checkbox
                          checked={groupedRightItems[category].every(i => i.checked)}
                          onChange={e => handleCategoryCheckRight(category, e.checked)}
                          onClick={e => e.stopPropagation()}
                        />
                        <span className="ms-2 fw-bold">{category}</span>
                      </div>
{groupedRightItems[category].map(item => (
  <div
    key={item.id}
    className="d-flex align-items-center pb-2 pt-2 px-3 border-bottom position-relative"
    style={{ minHeight: '56px' }}
  >
    <div className="d-flex align-items-center flex-grow-1 pe-5">
      <Checkbox
        className="ms-4 flex-shrink-0"
        checked={item.checked}
        onChange={e =>
          setRightItems(current =>
            current.map(i => (i.id === item.id ? { ...i, checked: e.checked } : i))
          )
        }
      />
      <span 
        className="ms-3 text-break hyphens-auto"
        style={{ 
          wordBreak: 'break-word', 
          lineHeight: '1.4',
          overflowWrap: 'break-word'
        }}
      >
        {item.label}
      </span>
    </div>
    <div className="position-absolute end-0 top-50 translate-middle-y pe-3">
      <InputSwitch
        checked={item.status}
        onChange={() => handleToggleStatus(item.id)}
      />
    </div>
  </div>
))}
                    </div>
                  )
                ))
            )}
          </div>
        </div>
      </div>
      <div className="row mt-3">
        <div className="col-12 d-flex justify-content-end gap-3">
              <Button
                label="Cancel"
                className="btn btn-outline px-5"
                onClick={handleCancel}
              />
              <Button
                label="Preview"
                className="btn btn-primary py-2 px-5"
                onClick={handlePreviewClick}
                disabled={!hasChanges}
              />
        </div>
      </div>
      <Dialog
        header={<div className="w-100 text-center">Preview</div>}
        visible={showChangesModal}
        style={{ width: '40vw', height: '100vh' }}
        contentStyle={{ maxHeight: '100vh', overflowY: 'auto' }}
        footer={modalFooter}
        onHide={() => setShowChangesModal(false)}
      >
        <div className="mb-4">
          {assigned.length > 0 && (
            <div className="mb-4">
              <h5 className=''>Added to Selected Repository</h5>
              <ul className="list-group">
                {assigned.map(item => (
                  <li key={item.id} className="list-group-item text-primary d-flex justify-content-between align-items-center"> <span>
              {item.label}
            </span>
            <span
              className={`badge ${
                item.status ? "bg-success" : "bg-danger"
              }`}
            >
              {item.status ? "Enable" : "Disable"}
            </span></li>
                ))}
              </ul>
            </div>
          )}

          {removed.length > 0 && (
            <div className="mb-4">
              <h5 className=''>Removed to Master's Repository</h5>
              <ul className="list-group">
                {removed.map(item => (
                  <li key={item.id} className="list-group-item text-primary">{item.label}</li>
                ))}
              </ul>
            </div>
          )}

          {statusChanged.length > 0 && (
            <div>
              <h5 className=''>Status Changes</h5>
              <ul className="list-group">
                {statusChanged.map(item => (
                  <li key={item.id} className="list-group-item  text-primary d-flex justify-content-between align-items-center">
                     <span>
              {item.label}
            </span>
            <span
              className={`badge ${
                item.status ? "bg-success" : "bg-danger"
              }`}
            >
              {item.status ? "Enable" : "Disable"}
            </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Dialog>
    </div>
  );
};

export default CourseDocuments;